import {
  useWSModalCustom,
  useWSSnackbar,
  WSForm,
  WSInputNumber,
  WSList,
  WSLoader,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { Currency, InvoiceRow } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import * as Yup from "yup";
import { queriesInvoiceRows, queriesInvoices } from "@query";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";

type FormData = {
  amount: number;
};

type Props = {
  invoice: InvoiceRow;
} & WSModalCustomProps<void>;

const ModalRefund: React.FC<Props> = props => {
  const { openSnackbar } = useWSSnackbar();
  // The row-menu only opens this modal for a real row, so the id is present.
  const invoiceId = props.invoice.id!;

  const [refundInvoice, refundInvoiceMeta] =
    queriesInvoices.refundInvoice.useMutation({
      dependencies: [
        queriesInvoiceRows.searchInvoiceRows.queryKeyBase,
        queriesInvoices.getInvoice.queryKeyBase
      ]
    });

  // The v3 search row carries no amount breakdown, so the refundable amount
  // (invoice total minus what's already refunded) comes from the full invoice.
  const queryInvoice = queriesInvoices.getInvoice.useQuery(
    { invoiceId },
    { enabled: !!invoiceId }
  );

  const currency = props.invoice.currency ?? Currency.USD;

  if (queryInvoice.isLoading || !queryInvoice.data) {
    return (
      <WSModal title="Refund invoice" size="S" onClose={props.onClose}>
        <WSLoader.Spinner />
      </WSModal>
    );
  }

  const fullInvoice = queryInvoice.data;
  const amountAvailableToRefund =
    (fullInvoice.amount ?? 0) - (fullInvoice.amountDetails?.refunds ?? 0);

  return (
    <WSForm<FormData>
      defaultValues={{ amount: amountAvailableToRefund }}
      validationSchema={Yup.object().shape({
        amount: Yup.number()
          .required("Required")
          .min(0.01, "Minimum $0.01")
          .max(
            amountAvailableToRefund,
            `You can refund up to ${formatMoney(
              amountAvailableToRefund,
              currency
            )}`
          )
      })}
      onSubmit={async formData => {
        await refundInvoice(
          {
            invoiceId,
            // `RefundRequest.reason` is declared on the v3 contract but the
            // runtime still answers `422 "reason not yet supported"`, so the
            // refund reason the v1 flow collected is not captured. Restore the
            // input here once the endpoint accepts it.
            refundRequest: { amount: formData.amount }
          },
          {
            onSuccess: () => {
              openSnackbar({ type: "success", message: "Refund issued" });
              props.onClose();
            }
          }
        );
      }}
    >
      <WSModal
        title="Refund invoice"
        size="S"
        onClose={props.onClose}
        actions={{
          buttons: [
            {
              label: "Cancel",
              kind: "Secondary",
              type: "button",
              onClick: () => props.onClose()
            },
            {
              label: "Refund",
              loading: refundInvoiceMeta.isLoading
            }
          ]
        }}
      >
        <WSList gap="M">
          <WSText color="gray500">
            You can refund up to{" "}
            {formatMoney(amountAvailableToRefund, currency)}
          </WSText>

          <WSForm.Field
            name="amount"
            label="Refund amount"
            component={WSInputNumber}
            componentProps={{
              currency,
              maxFractionDigits: 2,
              min: 0.01,
              max: amountAvailableToRefund
            }}
          />

          <WSErrorMessage
            contextKey="InvoiceRefund"
            error={refundInvoiceMeta.error}
          />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export const useModalRefund = () => useWSModalCustom(ModalRefund);
